import { createSlice, current } from '@reduxjs/toolkit';
import api from '../../api';
import { getDataFromUrl } from '../../utilities/urlResolver';
import { differenceInDays } from 'date-fns';
import { parseDate, findInArray } from '../../utilities/helpers';

const initialState = {
  pending: true,
  error: false,
  searchEngine: {
    where: {},
    when: {
      type: 'month',
      startDate: null,
      endDate: null,
      months: [],
      nights: [],
    },
    budget: {
      min: 100,
      max: 3000,
    },
    monthAhead: 10,
  },
  destinations: {
    list: [],
    selected: [],
    pending: true,
    error: false,
  },
};

export const mainFilterSlice = createSlice({
  name: "mainFilter",
  initialState,
  reducers: {
    getMainFiltersSuccess: (state, action) => {
      const {
        what,
        when,
        who,
        where: { destinations, destinations_groups: destinationsGroups },
        from_where: fromwhere,
        from_where_groups,
        budget,
        month_ahead,
      } = action.payload.searchengine;

      const { mainFilter: urlData } = getDataFromUrl('mainFilter');

      const getParsedBudget = () => {
        if (urlData.budget?.length === 2) {
          return {
            min: budget.min > urlData.budget[0] ? budget.min : urlData.budget[0],
            max: budget.max < urlData.budget[1] ? budget.max : urlData.budget[1]
          }
        }

        return budget
      }

      const parsedBudget = getParsedBudget();

      let selectedFromwhere = [{ id: '' }];
      if (localStorage.getItem('selectedFromwhere')) {
        const savedFromWhere = JSON.parse(localStorage.getItem('selectedFromwhere'));
        if (savedFromWhere.date > new Date().getTime()) {
          // get saved fromWhere data from localStorage
          selectedFromwhere = savedFromWhere.fromwhere;
        } else {
          // remove saved fromWhere from localStorage if it's outdated (365+ days old)
          localStorage.removeItem('selectedFromwhere');
        }
      }
      const userContext = JSON.parse(localStorage.getItem('hf_user'));
      if (userContext && userContext?.user?.userLevel === 'USER_EMAIL' && userContext?.user?.user_auth && userContext?.user?.originAirports?.length) {
        selectedFromwhere = fromwhere.filter((item) => (findInArray(userContext.user.originAirports, item.id)));
      }
      if (!selectedFromwhere.length || (selectedFromwhere.length && selectedFromwhere[0].id === '')) {
        selectedFromwhere = fromwhere.filter((item) => item.ip_selected);
      }
      const selectedFromWhereIds = selectedFromwhere.map(dp => dp.id);

      state.searchEngine = {
        fromwhere: fromwhere.map((item, i) => ({
          ...item,
          /* group_id: (i+1), */
          active: findInArray((urlData.fromwhere.length ? urlData.fromwhere : selectedFromWhereIds), item.id),
        })),
        from_where_groups: from_where_groups || {
          1: "West",
          2: "East",
          3: "South",
          4: "North",
          5: "Austria",
          6: "Switzerland"
        },
        what: what
          .sort((a, b) => b.priority - a.priority)
          .map((item) => ({
            ...item,
            active: urlData?.what?.some((type) => type === item.id),
          })),
        where: {
          destinations: destinations
            .sort((a, b) => a.priority - b.priority)
            .map((item) => ({
              ...item,
              active:
                urlData.where?.destinations?.some((i) => i === item.id) ||
                urlData.where?.destinationsGroups?.some((i) =>
                  item.destinations_groups?.includes(i)
                ) ||
                (item.atlas_top_id && (item.atlas_top_id === urlData.atlasTop)),
            })),
          destinationsGroups: destinationsGroups
            .sort((a, b) => b.priority - a.priority)
            .map((item) => ({
              ...item,
              active: urlData.where?.destinationsGroups?.some(
                (i) => i === item.id
              ),
            })),
        },
        budget: {
          initial: budget,
          ...parsedBudget,
        },
        when: {
          initial: {
            ...when,
            nightsList: Array.from({ length: when.flexible.max - when.flexible.min + 1 }, (_, i) => ({
              value: i + when.flexible.min,
              isSelected: false,
            }))
          },
          ...urlData.when,
        },
        who: {
          initial: who,
          adults: urlData.who.adults || 2,
          children: urlData.who.children || 0,
          rooms: urlData.who.rooms || 1,
          childAges: urlData.who.childAges || [],
        },
        monthAhead: month_ahead
      };

      if (!state.searchEngine.fromwhere.some((item) => item.active)) {
        state.searchEngine.fromwhere[0].active = true;
      }

      state.pending = false;
    },
    getMainFiltersFailed: (state) => ({
      ...state,
      pending: false,
      error: true,
    }),
    updateMainFilterFromUrl: (state) => {
      const currentState = current(state);
      const {
        what,
        when,
        who,
        where,
        fromwhere,
        budget,
        atlasTop,
        monthAhead,
        from_where_groups,
      } = currentState.searchEngine;

      const { mainFilter: urlData } = getDataFromUrl();
      const parsedBudget =
        urlData.budget?.length === 2
          ? {
              min: urlData.budget[0],
              max: urlData.budget[1],
            }
          : budget;

      let fromWhereValue = fromwhere.map((item) => ({
        ...item,
        active: findInArray(urlData.fromwhere, item.id),
      }));

      if (!fromWhereValue.find(i => i.active)) {
        // fromWhereValue[0].active = true;
        fromWhereValue = fromwhere.map((item) => ({
          ...item,
          active: item.ip_selected || false,
        }));
      }

      state.searchEngine = {
        fromwhere: fromWhereValue,
        from_where_groups: from_where_groups || {
          1: "West",
          2: "East",
          3: "South",
          4: "North",
          5: "Austria",
          6: "Switzerland"
        },
        what: what.map((item) => ({
          ...item,
          active: urlData?.what?.some((type) => type === item.id),
        })),
        where: {
          destinations: where.destinations.map((item) => ({
            ...item,
            active:
              urlData.where?.destinations?.some((i) => i === item.id) ||
              urlData.where?.destinationsGroups?.some((i) =>
                item.destinations_groups?.includes(i)
              ) ||
              (item.atlas_top_id && (item.atlas_top_id === atlasTop)),

          })),
          destinationsGroups: where.destinationsGroups.map((item) => ({
            ...item,
            active: urlData?.where.destinationsGroups?.some(
              (group) => group === item.id
            ),
          })),
        },
        budget: {
          ...budget,
          ...parsedBudget,
        },
        when: {
          ...when,
          ...urlData.when,
        },
        who: {
          ...who,
          adults: urlData.who.adults || 2,
          children: (urlData.who.children || 0) + (urlData.who.babies || 0),
          rooms: urlData.who.rooms || 1,
          childAges: urlData.who.childAges || [],
        },
        monthAhead,
      };
    },
    updateMainFilter: (state, action) => {
      const { updatedValue, filter } = action.payload;
      const currentState = current(state);

      if (filter === 'what') {
        state.searchEngine.what = currentState.searchEngine.what.map((item) => ({
          ...item,
          active: updatedValue.find((i) => i.id === item.id)?.active,
        }))
      } else {
        state.searchEngine[filter] = updatedValue;
      }

      if (filter === "where") {
        updatedValue.destinationsGroups.map((group) => {
          if (group.active) {
            updatedValue.destinations = updatedValue.destinations.map(
              (destination) => {
                if (
                  Array.isArray(destination.destinations_groups) &&
                  destination.destinations_groups.some((id) => id === group.id)
                ) {
                  return {
                    ...destination,
                    active: true,
                  };
                }

                return destination;
              }
            );
          }

          return group;
        });

        if (updatedValue.destinations?.filter(({ active }) => active).length === 1) {
          const newState = {
            ...currentState.searchEngine,
            where: updatedValue,
          };

          setSearchToLocalStorage(newState)
        }
      }
    },
    updateDatesFilter: (state, action) => {
      const currentState = current(state);
      const { startDate, endDate } = action.payload;

      if (
        action.payload.type === 'specific'
        && currentState.searchEngine.where?.destinations?.filter(({ active }) => active).length === 1
      ) {
        setSearchToLocalStorage(currentState.searchEngine)
      }

      state.searchEngine.when = {
        initial: state.searchEngine.when.initial,
        ...action.payload,
        startDate: startDate || null,
        endDate: endDate || null,
      };
    },
    updateWhoFilter: (state, action) => {
      state.searchEngine.who = {
        ...state.searchEngine.who,
        initial: state.searchEngine.who.initial,
        ...action.payload,
      };
    },
    updateBudgetFilter: (state, action) => {
      state.searchEngine.budget = {
        initial: state.searchEngine.budget.initial,
        min: action.payload[0],
        max: action.payload[1],
      };
    },
    getDestinationsSuccess: (state, action) => {
      state.destinations = {
        ...state.destinations,
        list: action.payload.map((destination) => {
          if (destination.type === 1) {
            return {
              ...destination,
              active: false,
            };
          }

          return destination;
        }),
        pending: false,
      };
    },
    getDestinationsFailed: (state) => {
      state.destinations = {
        list: [],
        error: true,
        pending: false,
      };
    },
    applyHistorySearch: (state, action) => {
      state.searchEngine = action.payload.data;
    },
    setSingleDestination: (state, action) => {
      state.searchEngine.where = {
        destinationsGroups: state.searchEngine.where.destinationsGroups.map(item => ({
          ...item,
          active: false,
        })),
        destinations: state.searchEngine.where.destinations.map(item => ({
          ...item,
          active: +action.payload === +item.id
        }))
      }
    },
    setMainFilterDefault: (state) => {
      return {
        ...state,
        searchEngine: {
          ...state.searchEngine,
          when: {
            ...state.searchEngine.when,
            type: 'month',
            startDate: null,
            endDate: null,
            months: [],
            nights: [],
          },
          budget: {
            initial: state.searchEngine.budget.initial,
            min: state.searchEngine.budget.initial.min,
            max: state.searchEngine.budget.initial.max,
          },
          what: state.searchEngine.what.map((item) => ({
            ...item,
            active: false,
          })),
          who: {
            ...state.searchEngine.who,
            adults: 2,
            children: 0,
            childAges: [],
            rooms: 1,
          },
          where: {
            ...state.searchEngine.where,
            destinationsGroups: state.searchEngine.where.destinationsGroups.map((item) => ({
              ...item,
              active: false,
            })),
            destinations: state.searchEngine.where.destinations.map((item) => ({
              ...item,
              active: false,
            }))
          }
        }
      }
    },
    setNearbyAirports: (state, action) => {
      const currentState = current(state);
      state.searchEngine.fromwhere = currentState.searchEngine.fromwhere.map((item) => ({
        ...item,
        active: !!action.payload.find((i) => i.id === item.id),
        ip_selected: !!action.payload.find((i) => i.id === item.id),
      }));
    }
  },
});

const setSearchToLocalStorage = (state) => {
  const { when, where, who } = state;

  if (
    !when.startDate ||
    !when.endDate ||
    differenceInDays(parseDate(when.endDate), parseDate(when.startDate)) === 0
  ) {
    return;
  }

  const getSearchParamsKey = () =>
    `${where.destinations?.find(({ active }) => active).id}:` +
    `${when.startDate}-${when.endDate}` +
    `:${who.adults}:${who.children}`;

  let recentSearches = JSON.parse(localStorage.getItem("_recentSearches")) ?? [];
  recentSearches = recentSearches.filter(
    ({ key }) => key !== getSearchParamsKey()
  );

  recentSearches = recentSearches.slice(0, 50)

  localStorage.setItem(
    "_recentSearches",
    JSON.stringify([
      {
        key: getSearchParamsKey(),
        data: state,
      },
      ...recentSearches,
    ])
  );
}

export const {
  getMainFiltersSuccess,
  getMainFiltersFailed,
  getDestinationsSuccess,
  getDestinationsFailed,
  updateMainFilter,
  updateDatesFilter,
  updateWhoFilter,
  updateBudgetFilter,
  applyHistorySearch,
  updateMainFilterFromUrl,
  setSingleDestination,
  setMainFilterDefault,
  setNearbyAirports,
} = mainFilterSlice.actions;

export const fetchMainFilterData = () => async (dispatch) => {
  try {
    const response = await api.fetchMainFilters();

    dispatch(getMainFiltersSuccess(response.data));
  } catch (error) {
    dispatch(getMainFiltersFailed());
  }
};

export default mainFilterSlice.reducer;
